import { Layout } from '@components/common'
import { Button, LoadingDots, SelectDropDown } from '@components/ui'
import { Input } from '@components/ui/Form'
import getGlobalStaticProps from '@lib/globalStaticProps'
import processLocale, { LOCALE_SWITCHER, REGION_OPTIONS } from '@lib/locale'
import { SliceZone } from '@prismicio/react'
import { components } from '@slices/index'
import type { GetStaticPropsContext, InferGetStaticPropsType } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { createClient, getSingleDocByType } from 'prismicio'
import tradeFanMasterBookImage from 'public/trade-accounts/trade-fan-deck-master-book.jpg'
import tradeSamplePotsImage from 'public/trade-accounts/trade-sample-pots.jpg'
import tradeStudioImage from 'public/trade-accounts/trade-studio.jpg'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

export async function getStaticProps({
  locale,
  previewData,
}: GetStaticPropsContext) {
  const client = createClient({ previewData })
  const globalProps = await getGlobalStaticProps(client, locale)
  const doc = await getSingleDocByType('trade_accounts', client, locale)

  const translations = await serverSideTranslations(locale ?? 'en-au', [
    'common',
    'trade-accounts',
  ])
  return {
    props: {
      ...translations,
      ...globalProps,
      doc,
    },
    //revalidate: 200,
  }
}

export default function TradeAccounts({
  doc,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
  } = useForm({ mode: 'onChange' })
  const { t } = useTranslation(['trade-accounts', 'common'])
  const [acceptNotifications, setAcceptNotifications] = useState(false)
  const { locale } = useRouter()
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const currentLocale = processLocale(locale)
  const currentItem = LOCALE_SWITCHER.find(
    (item) => item.value === currentLocale
  )!

  const i18nRegions = REGION_OPTIONS.map(({ name, value }) => {
    let i18nKey = name.replaceAll(' / ', '-').replaceAll(' ', '-').toLowerCase()
    return { name: t(i18nKey, { ns: 'common' }), value }
  })

  const defaultRegionOption = i18nRegions.find(
    (opt) => opt.value === currentItem.region
  )!

  const [regionsOptions] = useState(i18nRegions)
  const [selectedRegion, setSelectedRegion] = useState(defaultRegionOption)
  const onSubmit = async (data: any) => {
    if (isValid) {
      data.acceptNotifications = acceptNotifications
      data.locale = locale
      setLoading(true)
      try {
        const response = await fetch(`/api/trade-accounts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
        const result = await response.json()

        if (result?.handle) {
          setSuccess(true)
          setLoading(false)
          window.scrollTo({ top: 0, behavior: 'smooth' })
        } else {
          setSuccess(false)
          setLoading(false)
        }
      } catch (err) {
        setSuccess(false)
        setLoading(false)
      }
    }
  }
  const handleAcceptsNotificationsChange = () => {
    setAcceptNotifications(!acceptNotifications)
  }

  const handleRegionSelection = (option: { name: string; value: string }) => {
    setSelectedRegion(option) // Update the selected region state
    setValue('region', option.value.toUpperCase(), { shouldValidate: true }) // Update the form value for 'region'
  }
  useEffect(() => {
    setValue('region', selectedRegion.value.toUpperCase(), {
      shouldValidate: true,
    })
  }, [selectedRegion, setValue])

  return (
    <div className=" py-30">
      {success ? (
        <div className="wrapper flex flex-col items-center gap-4 text-center text-dark-grey">
          <div className="flex flex-col gap-4 md:flex-row">
            <Image
              alt="trade studio"
              src={tradeStudioImage}
              placeholder="blur"
              width={250}
              height={375}
            />
            <Image
              alt="sample pots"
              placeholder="blur"
              src={tradeSamplePotsImage}
              width={250}
              height={375}
            />
            <Image
              alt="Fan Deck Master Book Images Lajos Varga"
              placeholder="blur"
              src={tradeFanMasterBookImage}
              width={250}
              height={375}
            />
          </div>
          <h3 className="heading-mobile-3 lg:heading-3 whitespace-nowrap">
            {t('thankYou.label')}
          </h3>
          <p>
            <p className="body-1 text-center">
              {t('thankYou.receivedTradeRequrest')}
            </p>
            <p className="body-1 text-center">
              {t('thankYou.reviewInformationAndInTouch')}
            </p>
          </p>
        </div>
      ) : (
        <>
          <SliceZone slices={doc?.data?.slices} components={components} />
          <div className="wrapper grid grid-cols-12 overflow-hidden pt-10">
            <div className="col-span-12 col-start-1 flex flex-col items-center justify-center lg:col-span-4 lg:col-start-5">
              <form
                noValidate
                onSubmit={handleSubmit(onSubmit)}
                className="flex w-full flex-col gap-4"
              >
                <Input
                  register={register}
                  type="text"
                  name="name"
                  label={`${t('name.label')} *`}
                  placeHolder={`${t('name.placeholder')}`}
                  validation={{ required: true }}
                  error={errors.name}
                />
                <Input
                  register={register}
                  type="email"
                  name="email"
                  label={`${t('email.label')} *`}
                  placeHolder={`${t('email.placeholder')}`}
                  validation={{
                    required: true,
                    pattern:
                      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/i,
                  }}
                  error={errors.email}
                />
                <Input
                  register={register}
                  type="text"
                  name="line1"
                  label={`${t('line1.label')} *`}
                  placeHolder={`${t('line1.placeholder')}`}
                  validation={{ required: true }}
                  error={errors.line1}
                />
                <Input
                  register={register}
                  type="text"
                  name="line2"
                  label={`${t('line2.label')} `}
                  placeHolder={`${t('line2.placeholder')}`}
                />
                <Input
                  register={register}
                  type="text"
                  name="suburb"
                  label={`${t('suburb.label')} *`}
                  placeHolder={`${t('suburb.placeholder')}`}
                  validation={{ required: true }}
                  error={errors.suburb}
                />
                <Input
                  register={register}
                  type="text"
                  name="postcode"
                  label={`${t('postcode.label')} *`}
                  placeHolder={`${t('postcode.placeholder')}`}
                  validation={{ required: true }}
                  error={errors.postcode}
                />
                <Input
                  register={register}
                  type="text"
                  name="country"
                  label={`${t('country.label')} *`}
                  placeHolder={`${t('country.placeholder')}`}
                  validation={{ required: true }}
                  error={errors.country}
                />
                <Input
                  register={register}
                  type="tel"
                  name="mobile"
                  label={`${t('mobile.label')} *`}
                  placeHolder={`${t('mobile.placeholder')}`}
                  validation={{ required: true }}
                  error={errors.mobile}
                />
                <Input
                  register={register}
                  type="tel"
                  name="officeNumber"
                  label={`${t('officeNumber.label')} *`}
                  placeHolder={`${t('officeNumber.placeholder')}`}
                  validation={{ required: true }}
                  error={errors.officeNumber}
                />
                <Input
                  register={register}
                  type="text"
                  name="businessName"
                  label={`${t('businessName.label')} *`}
                  placeHolder={`${t('businessName.placeholder')}`}
                  validation={{ required: true }}
                  error={errors.businessName}
                />
                <Input
                  register={register}
                  type="text"
                  name="businessType"
                  label={`${t('businessType.label')} *`}
                  placeHolder={`${t('businessType.placeholder')}`}
                  validation={{ required: true }}
                  error={errors.businessType}
                />
                <Input
                  register={register}
                  type="text"
                  name="taxRegistrationNumber"
                  label={`${t('taxRegistrationNumber.label')}`}
                  placeHolder={`${t('taxRegistrationNumber.placeholder')}`}
                  validation={{ required: false }}
                  error={errors.taxRegistrationNumber}
                />
                <div
                  className={`label pt-2 ${
                    errors.professions && 'text-inputError'
                  }`}
                >
                  <span className="label-bold">{`${t(
                    'professions.label'
                  )}: `}</span>
                  ({t('professions.help')}) *
                </div>
                <div className="flex flex-col gap-3 pb-2">
                  <label className="containerBox">
                    <input
                      {...register('professions', { required: true })}
                      type="checkbox"
                      value={t('professions.designer') as string}
                    />
                    <p className={`${errors.professions && 'text-inputError'}`}>
                      {t('professions.designer')}
                    </p>
                    <span
                      className={`checkmark ${
                        errors.professions && 'checkmarkError'
                      }`}
                    ></span>
                  </label>
                  <label className="containerBox">
                    <input
                      {...register('professions')}
                      type="checkbox"
                      value="Architect"
                    />
                    <p className={`${errors.professions && 'text-inputError'}`}>
                      {t('professions.architect')}
                    </p>
                    <span
                      className={`checkmark ${
                        errors.professions && 'checkmarkError'
                      }`}
                    ></span>
                  </label>
                  <label className="containerBox">
                    <input
                      {...register('professions')}
                      type="checkbox"
                      value="Paint Applicator"
                    />
                    <p className={`${errors.professions && 'text-inputError'}`}>
                      {t('professions.paintApplicator')}
                    </p>
                    <span
                      className={`checkmark ${
                        errors.professions && 'checkmarkError'
                      }`}
                    ></span>
                  </label>
                  <label className="containerBox">
                    <input
                      {...register('professions')}
                      type="checkbox"
                      value={t('professions.storeOwner') as string}
                    />
                    <p className={`${errors.professions && 'text-inputError'}`}>
                      {t('professions.storeOwner')}
                    </p>
                    <span
                      className={`checkmark ${
                        errors.professions && 'checkmarkError'
                      }`}
                    ></span>
                  </label>
                </div>

                <div className="flex flex-col gap-4 pt-2 pb-4">
                  <Input
                    register={register}
                    type="textarea"
                    name="activitiesDescription"
                    label={`${t('activitiesDescription.label')} *`}
                    placeHolder={`${t('activitiesDescription.placeholder')}`}
                    validation={{ required: true }}
                    error={errors.activitiesDescription}
                    rows={3}
                  />
                  <Input
                    register={register}
                    type="textarea"
                    name="cooperationDescription"
                    label={`${t('cooperationDescription.label')} *`}
                    placeHolder={`${t('cooperationDescription.placeholder')}`}
                    validation={{ required: true }}
                    error={errors.cooperationDescription}
                    rows={3}
                  />
                  <Input
                    register={register}
                    type="text"
                    name="reference"
                    label={`${t('reference.label')}? *`}
                    placeHolder={`${t('reference.placeholder')}`}
                    validation={{ required: true }}
                    error={errors.reference}
                  />
                  <div>
                    <div className="label-bold pb-2">{t('region.label')}</div>
                    <SelectDropDown
                      options={regionsOptions}
                      arialLabel="Region selector"
                      selectedOption={selectedRegion}
                      handleOptionSelection={handleRegionSelection}
                    />
                  </div>
                </div>
                <label className="containerBox pb-2">
                  <input
                    type="checkbox"
                    checked={acceptNotifications}
                    onChange={handleAcceptsNotificationsChange}
                  />
                  {t('acceptNotifications.label')}
                  <span className="checkmark"></span>
                </label>
                <Button
                  aria-label={t('sendApplication.label') as string}
                  type="submit"
                  width="100%"
                  className="h-12"
                  disabled={loading}
                  onClick={onSubmit}
                >
                  {loading ? <LoadingDots /> : t('sendApplication.label')}
                </Button>
              </form>
            </div>
          </div>
        </>
      )}
      <style jsx>
        {`
          .containerBox {
            display: block;
            position: relative;
            padding-left: 24px;
            cursor: pointer;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
          }
          .containerBox input {
            position: absolute;
            opacity: 0;
            cursor: pointer;
            height: 0;
            width: 0;
          }
          .checkmark {
            position: absolute;
            top: 3px;
            left: 0;
            height: 16px;
            width: 16px;
            border: 1px solid #b1b1b1;
            border-radius: 2px;
            background: #fafafa;
          }
          .checkmarkError {
            border: 1px solid #df3c36;
          }
          .checkmark:hover {
            border: 1px solid #646464;
          }
          .containerBox input:checked ~ .checkmark {
            background-color: #646464;
            border: none;
          }
          .containerBox input:checked ~ .checkmark:hover {
            background: #464646;
          }
          .checkmark:after {
            content: '';
            position: absolute;
            display: none;
          }
          .containerBox input:checked ~ .checkmark:after {
            display: block;
          }
          .containerBox .checkmark:after {
            left: 6px;
            top: 2px;
            width: 5px;
            height: 10px;
            border: solid white;
            border-width: 0 2px 2px 0;
            -webkit-transform: rotate(45deg);
            -ms-transform: rotate(45deg);
            transform: rotate(45deg);
          }
        `}
      </style>
    </div>
  )
}

TradeAccounts.Layout = Layout
